import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

type Booking = {
  apartment: string;
  total_amount: number;
  checkin: string;
  payment_status: string;
};

export default function AdminRevenue() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('bookings').select('apartment, total_amount, checkin, payment_status');
      setBookings((data as Booking[]) ?? []);
    }
    load();
  }, []);

  const yearBookings = bookings.filter(b => b.checkin.startsWith(String(year)));
  const totalRevenue = yearBookings.reduce((s, b) => s + Number(b.total_amount), 0);
  const heraRevenue = yearBookings.filter(b => b.apartment === 'hera').reduce((s, b) => s + Number(b.total_amount), 0);
  const zeusRevenue = yearBookings.filter(b => b.apartment === 'zeus').reduce((s, b) => s + Number(b.total_amount), 0);
  const paidRevenue = yearBookings.filter(b => b.payment_status === 'fully_paid').reduce((s, b) => s + Number(b.total_amount), 0);

  const months = Array.from({ length: 12 }, (_, i) => {
    const m = String(i + 1).padStart(2, '0');
    const monthBookings = yearBookings.filter(b => b.checkin.substring(5, 7) === m);
    return {
      label: new Date(year, i).toLocaleString('en', { month: 'short' }),
      total: monthBookings.reduce((s, b) => s + Number(b.total_amount), 0),
      count: monthBookings.length,
    };
  });

  const maxMonth = Math.max(...months.map(m => m.total), 1);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-light">Revenue</h1>
        <div className="flex gap-2">
          {[year - 1, year, year + 1].map(y => (
            <button key={y} onClick={() => setYear(y)}
              className={`px-3 py-1.5 text-xs tracking-wider rounded-sm transition-colors ${year === y ? 'bg-accent text-accent-foreground' : 'bg-card text-muted-foreground border border-border'}`}>
              {y}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Revenue', value: `€${totalRevenue.toLocaleString()}` },
          { label: 'Hera Revenue', value: `€${heraRevenue.toLocaleString()}` },
          { label: 'Zeus Revenue', value: `€${zeusRevenue.toLocaleString()}` },
          { label: 'Confirmed Paid', value: `€${paidRevenue.toLocaleString()}` },
        ].map(c => (
          <div key={c.label} className="bg-card border border-border rounded-sm p-5">
            <p className="font-serif text-2xl font-light">{c.value}</p>
            <p className="text-sm text-muted-foreground mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Monthly bar chart */}
      <div className="bg-card border border-border rounded-sm p-6">
        <h3 className="font-serif text-xl mb-6">Monthly Breakdown</h3>
        <div className="flex items-end gap-2 h-48">
          {months.map(m => (
            <div key={m.label} className="flex-1 flex flex-col items-center gap-1">
              <p className="text-xs font-medium">€{m.total > 0 ? m.total.toLocaleString() : ''}</p>
              <div className="w-full bg-accent/20 rounded-t-sm relative" style={{ height: `${(m.total / maxMonth) * 100}%`, minHeight: m.total > 0 ? 4 : 0 }}>
                <div className="absolute inset-0 bg-accent rounded-t-sm" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{m.label}</p>
              {m.count > 0 && <p className="text-[10px] text-muted-foreground">{m.count} bookings</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
